import ContentLayout from '@/components/ui/admin/ContentLayout';
// import { Stack, Grid, Box, Input, Paper } from '@mui/material';
import { useState } from 'react';

import {
  Grid,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Box,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';

import { blogSchema, BlogFormValues } from "./../validations/blogFormValidation";
import { blogService } from '../services/blog.service';
import { useForm, SubmitHandler, Controller, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FileUploadField from '../../../../components/ui/FileUploadField';
import CustomAlert from '@/components/ui/CustomAlert';
import { Blog } from '../types/blog.type';

import RichTextEditor from '../../../../components/form/RichTextEditor';


export default function BlogForm() {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BlogFormValues>({
    resolver: zodResolver(blogSchema) as Resolver<BlogFormValues>,
    defaultValues: {
      title: 'sadf',
      content: 'fsdfsd',
      excerpt: 'fsdfsd',
      meta_title: '',
      meta_description: '',
      image: null,
      status: 'draft',
    },
  });
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data: BlogFormValues, status: 'draft' | 'published' | '') => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      // append normal fields
      formData.append("title", data.title);
      formData.append("content", data.content);
      formData.append("meta_title", data.meta_title ?? '');
      formData.append("meta_description", data.meta_description ?? '');
      formData.append("excerpt", data.excerpt ?? '');
      formData.append("status", status ?? 'draft');      // append files
      if (data.image instanceof File) {
        formData.append("image", data.image);
      }
      await blogService.create(formData);
      reset();
    } catch (error: any) {

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContentLayout
      title="New Blog"
      backLink={{ label: 'Back to Blogs', to: '/admin/blogs' }}
      description="Create a new blog entry by filling out the form below."
    >
      <Box
        component="form"
        onSubmit={handleSubmit((data) => onSubmit(data, 'draft'))}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={2}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <TextField
                  {...register('title')}
                  fullWidth
                  label="Enter Title"
                  placeholder='Enter Title'
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  disabled={isLoading}
                  required
                />
              </Paper>

              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1 }}
                >
                  Content
                </Typography>

                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoading}
                      // error={!!errors.content}
                    />
                  )}
                />

                {errors.content && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {errors.content.message}
                  </Typography>
                )}
              </Paper>
              <Paper elevation={2} sx={{ p: 2 }}>
                <TextField
                  {...register('excerpt')}
                  fullWidth
                  label="Enter Excerpt"
                  placeholder='Enter Excerpt'
                  error={!!errors.excerpt}
                  helperText={errors.excerpt?.message}
                  disabled={isLoading}
                />
              </Paper>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              <Paper elevation={2} sx={{ p: 2 }}>
                {/* Submit Button */}
                <Stack sx={{
                  gap: 2,
                  justifyContent: 'flex-end'
                }} direction="row">
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={isLoading}
                    onClick={handleSubmit((data) => onSubmit(data, 'draft'))}
                  >
                    Save as Draft
                  </Button>
                  <Button
                    onClick={handleSubmit((data) => onSubmit(data, 'published'))}
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    startIcon={isLoading && <CircularProgress size={20} />}
                  >
                    Publish Blog
                  </Button>
                </Stack>
              </Paper>
              <Paper elevation={2} sx={{ p: 2 }}>
                <FileUploadField
                  name="image"
                  control={control}
                  label="Thumbnail"
                  accept="image/*"
                  error={errors.image?.message}
                  disabled={isLoading}
                />
              </Paper>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Stack spacing={3}>
                  <TextField
                    {...register('meta_title')}
                    fullWidth
                    label="Meta Title"
                    placeholder='Enter Meta Title'
                    error={!!errors.meta_title}
                    helperText={errors.meta_title?.message}
                    disabled={isLoading}
                  />
                  <TextField
                    {...register('meta_description')}
                    fullWidth
                    label="Meta Description"
                    placeholder='Enter Meta Description'
                    error={!!errors.meta_description}
                    helperText={errors.meta_description?.message}
                    disabled={isLoading}
                    multiline
                    rows={4}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </ContentLayout>

  );
}